/**
 * Module-level SWR-style cache for fetcher responses.
 *
 * Used by Intelligence pages (/backtesting, /batch, /factors, /ml-insights,
 * /strategies) that fetch with raw useState+useEffect rather than react-query.
 *
 * Pattern:
 *   - Sidebar hover -> prefetchRouteData -> warms cache via cachedFetch
 *   - Page mount -> same cachedFetch -> served from cache (no second round-trip)
 *   - Repeat navigation within TTL -> instant render from cache
 *
 * In-flight requests are deduped. Rejected promises are NOT cached.
 */

type Entry = {
  value?: unknown
  expires: number
  pending?: Promise<unknown>
}

const cache = new Map<string, Entry>()
const DEFAULT_TTL_MS = 60_000

export function getCached<T>(key: string): T | undefined {
  const e = cache.get(key)
  if (!e || e.pending) return undefined
  if (e.expires <= Date.now()) {
    cache.delete(key)
    return undefined
  }
  return e.value as T
}

export function hasCached(key: string): boolean {
  return getCached<unknown>(key) !== undefined
}

export function setCached<T>(key: string, value: T, ttlMs = DEFAULT_TTL_MS): void {
  cache.set(key, { value, expires: Date.now() + ttlMs })
}

export function invalidateCached(key: string): void {
  cache.delete(key)
}

export function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS,
): Promise<T> {
  const e = cache.get(key)
  const now = Date.now()
  if (e && !e.pending && e.expires > now) {
    return Promise.resolve(e.value as T)
  }
  if (e?.pending) {
    return e.pending as Promise<T>
  }
  const pending = fetcher()
    .then((value) => {
      cache.set(key, { value, expires: Date.now() + ttlMs })
      return value
    })
    .catch((err) => {
      cache.delete(key)
      throw err
    })
  cache.set(key, { expires: 0, pending: pending as Promise<unknown> })
  return pending
}

/**
 * Fire-and-forget prefetch. Errors are swallowed (the page mount will
 * surface them when it really needs the data).
 */
export function prefetchCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS,
): void {
  cachedFetch(key, fetcher, ttlMs).catch(() => {})
}
